"use client";

import { useState } from "react";
import { toast } from "@/lib/toast";
import { Notification } from "@/types/notification";
import { postWithAuth } from "@/lib/api-helpers";
import NotificationItem, {
  NotificationAvatar,
  NotificationWorkspaceLink,
} from "@/components/tailwind/notification/notificationItem";
import { useWorkspaceContext } from "@/contexts/workspaceContext";

interface NotificationWorkAreaJoinRequestProps {
  notification: Notification;
}

export default function NotificationWorkAreaJoinRequest({
  notification,
}: NotificationWorkAreaJoinRequestProps) {
  const { getMemberName } = useWorkspaceContext();
  const displayName = getMemberName(notification.createdBy?.userId) || getMemberName(notification.createdBy?.userEmail) || notification.createdBy?.userName || "Someone";
  const workAreaName = notification.context?.workAreaName || "a workarea";
  const workAreaId = notification.context?.workAreaId;
  const requesterUserId = notification.createdBy?.userId;

  // Local-only state — we keep this around so the buttons hide immediately
  // after a successful decision (the actual `responsed` flag on the doc is
  // set server-side and shows up after the next /get-all refetch).
  const [decided, setDecided] = useState<null | "APPROVE" | "REJECT">(null);
  const [busy, setBusy] = useState<boolean>(false);

  const alreadyResponded = Boolean(notification.responsed) || decided !== null;

  const respond = async (decision: "APPROVE" | "REJECT") => {
    if (!workAreaId || !requesterUserId) {
      toast.error("Missing workarea or requester info on this notification.");
      return;
    }
    setBusy(true);
    try {
      const res: any = await postWithAuth("/api/workarea/respond-join-request", {
        workAreaId,
        requesterUserId,
        decision,
        originalRequestId: notification._id,
      });
      if (res?.success || res?.decision) {
        setDecided(decision);
        toast.success(
          decision === "APPROVE" ? "Request approved" : "Request declined"
        );
      } else if (res?.message) {
        toast.error(res.message);
      } else {
        toast.error("Could not respond to the request.");
      }
    } catch (err) {
      console.error("Failed to respond to workarea join request:", err);
      toast.error("Network error. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <NotificationItem
      icon={<NotificationAvatar name={displayName} userId={notification.createdBy?.userId} email={notification.createdBy?.userEmail} />}
      createdAt={notification.createdAt}
      unread={!notification.read}
      title={displayName}
      message={
        <>
          Requested to join{" "}
          <span className="font-medium text-gray-700 dark:text-gray-300">
            {workAreaName}
          </span>{" "}
          in{" "}
          <NotificationWorkspaceLink
            workspaceId={notification.workspaceId}
            workspaceName={notification.workspaceName}
          />
          {alreadyResponded ? (
            <span className="mt-1 block italic">
              {decided === "APPROVE"
                ? "You approved this request."
                : decided === "REJECT"
                  ? "You declined this request."
                  : "Responded."}
            </span>
          ) : null}
        </>
      }
      actions={
        !alreadyResponded ? (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => respond("APPROVE")}
              className="rounded-md border  bg-white px-2.5 py-1 text-[13px] font-medium text-gray-700 shadow-sm transition hover-component disabled:opacity-50 border-component dark:bg-transparent dark:text-gray-200 "
            >
              Approve
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => respond("REJECT")}
              className="rounded-md border  bg-white px-2.5 py-1 text-[13px] font-medium text-gray-700 shadow-sm transition hover-component disabled:opacity-50 border-component dark:bg-transparent dark:text-gray-200 "
            >
              Deny
            </button>
          </>
        ) : undefined
      }
    />
  );
}
